/**
 * Minimal orbit camera for the volume renderer.
 *
 * Coordinate convention:
 *   - The volume sits in the unit cube [0,1]³ after normalisation.
 *   - The camera orbits a target point (default = cube centre).
 *   - Azimuth = rotation around world-Y (0 = looking +Z), positive =
 *     rotate to the right.
 *   - Elevation = tilt up/down; clamped to ±89° so we don't flip poles.
 *   - Distance is in unit-cube units.
 *
 * No external matrix lib — everything is plain TypedArray math. Gives
 * us a ~1 KB gz footprint and no chance of a shader-incompatible
 * matrix handedness surprise six months from now.
 */

export type Vec3 = [number, number, number];
export type Mat4 = Float32Array; // column-major, 16 floats, matches WebGL

export interface CameraState {
  target: Vec3;
  azimuth:   number;     // radians
  elevation: number;     // radians
  distance:  number;     // cube units (≈ voxel side * sqrt(3))
  fovY:      number;     // radians
}

export function defaultCamera(): CameraState {
  return {
    target: [0.5, 0.5, 0.5],
    azimuth:   0,
    elevation: 0,
    distance:  3.0,
    fovY:      Math.PI / 4,
  };
}

const DEG = Math.PI / 180;
const MAX_EL = 89 * DEG;

export function rotate(cam: CameraState, dxPx: number, dyPx: number, canvasH: number) {
  // Full-height drag = 180°.
  const k = Math.PI / Math.max(1, canvasH);
  cam.azimuth   -= dxPx * k;
  cam.elevation += dyPx * k;
  if (cam.elevation >  MAX_EL) cam.elevation =  MAX_EL;
  if (cam.elevation < -MAX_EL) cam.elevation = -MAX_EL;
}

export function dolly(cam: CameraState, delta: number) {
  const f = Math.pow(1.1, delta);
  cam.distance *= f;
  if (cam.distance < 0.3) cam.distance = 0.3;
  if (cam.distance > 10 ) cam.distance = 10;
}

/** Pan in the plane perpendicular to the view ray.
 *  `dx` and `dy` are screen-pixel offsets. */
export function pan(cam: CameraState, dxPx: number, dyPx: number, canvasH: number) {
  // Scale pan proportional to distance + fov — feels "natural" at any
  // zoom level.
  const worldPerPx = 2 * cam.distance * Math.tan(cam.fovY / 2) / canvasH;
  const [right, up] = basis(cam);
  cam.target[0] -= right[0] * dxPx * worldPerPx - up[0] * dyPx * worldPerPx;
  cam.target[1] -= right[1] * dxPx * worldPerPx - up[1] * dyPx * worldPerPx;
  cam.target[2] -= right[2] * dxPx * worldPerPx - up[2] * dyPx * worldPerPx;
}

/** Snap-to presets — look straight down the named axis. */
export function axialView   (cam: CameraState) { cam.azimuth = 0;             cam.elevation =  Math.PI / 2 - 0.0001; }
export function coronalView (cam: CameraState) { cam.azimuth = 0;             cam.elevation = 0; }
export function sagittalView(cam: CameraState) { cam.azimuth = Math.PI / 2;   cam.elevation = 0; }
export function reset       (cam: CameraState) {
  cam.azimuth = -Math.PI / 6;
  cam.elevation = Math.PI / 9;
  cam.distance = 3.0;
  cam.target = [0.5, 0.5, 0.5];
}

/** Camera position in world space. */
export function position(cam: CameraState): Vec3 {
  const cosE = Math.cos(cam.elevation);
  const x = cam.target[0] + cam.distance * cosE * Math.sin(cam.azimuth);
  const y = cam.target[1] + cam.distance * Math.sin(cam.elevation);
  const z = cam.target[2] + cam.distance * cosE * Math.cos(cam.azimuth);
  return [x, y, z];
}

/** Returns (right, up) unit vectors in world space. */
export function basis(cam: CameraState): [Vec3, Vec3] {
  const pos = position(cam);
  const fwd: Vec3 = [
    cam.target[0] - pos[0],
    cam.target[1] - pos[1],
    cam.target[2] - pos[2],
  ];
  normalize(fwd);
  const worldUp: Vec3 = [0, 1, 0];
  const right = cross(fwd, worldUp);
  normalize(right);
  const up = cross(right, fwd);
  normalize(up);
  return [right, up];
}

/** View matrix (world → view). Column-major Float32 for uniformMatrix4fv. */
export function viewMatrix(cam: CameraState): Mat4 {
  const eye = position(cam);
  const fwd: Vec3 = [
    cam.target[0] - eye[0],
    cam.target[1] - eye[1],
    cam.target[2] - eye[2],
  ];
  normalize(fwd);
  const worldUp: Vec3 = [0, 1, 0];
  const right = cross(fwd, worldUp);
  normalize(right);
  const up = cross(right, fwd);
  normalize(up);

  const m = new Float32Array(16);
  m[0] = right[0]; m[4] = right[1]; m[8]  = right[2];  m[12] = -dot(right, eye);
  m[1] = up[0];    m[5] = up[1];    m[9]  = up[2];     m[13] = -dot(up, eye);
  m[2] = -fwd[0];  m[6] = -fwd[1];  m[10] = -fwd[2];   m[14] =  dot(fwd, eye);
  m[3] = 0;        m[7] = 0;        m[11] = 0;         m[15] = 1;
  return m;
}

/** Perspective projection, right-handed, mapping to clip z in [-1, 1]. */
export function perspective(fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

export function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      out[c * 4 + r] =
        a[0  + r] * b[c * 4 + 0] +
        a[4  + r] * b[c * 4 + 1] +
        a[8  + r] * b[c * 4 + 2] +
        a[12 + r] * b[c * 4 + 3];
    }
  }
  return out;
}

export function invertM4(a: Mat4): Mat4 {
  const m = a;
  const inv = new Float32Array(16);
  inv[0]  =  m[5]*m[10]*m[15] - m[5]*m[11]*m[14] - m[9]*m[6]*m[15] + m[9]*m[7]*m[14] + m[13]*m[6]*m[11] - m[13]*m[7]*m[10];
  inv[4]  = -m[4]*m[10]*m[15] + m[4]*m[11]*m[14] + m[8]*m[6]*m[15] - m[8]*m[7]*m[14] - m[12]*m[6]*m[11] + m[12]*m[7]*m[10];
  inv[8]  =  m[4]*m[9]*m[15]  - m[4]*m[11]*m[13] - m[8]*m[5]*m[15] + m[8]*m[7]*m[13] + m[12]*m[5]*m[11] - m[12]*m[7]*m[9];
  inv[12] = -m[4]*m[9]*m[14]  + m[4]*m[10]*m[13] + m[8]*m[5]*m[14] - m[8]*m[6]*m[13] - m[12]*m[5]*m[10] + m[12]*m[6]*m[9];
  inv[1]  = -m[1]*m[10]*m[15] + m[1]*m[11]*m[14] + m[9]*m[2]*m[15] - m[9]*m[3]*m[14] - m[13]*m[2]*m[11] + m[13]*m[3]*m[10];
  inv[5]  =  m[0]*m[10]*m[15] - m[0]*m[11]*m[14] - m[8]*m[2]*m[15] + m[8]*m[3]*m[14] + m[12]*m[2]*m[11] - m[12]*m[3]*m[10];
  inv[9]  = -m[0]*m[9]*m[15]  + m[0]*m[11]*m[13] + m[8]*m[1]*m[15] - m[8]*m[3]*m[13] - m[12]*m[1]*m[11] + m[12]*m[3]*m[9];
  inv[13] =  m[0]*m[9]*m[14]  - m[0]*m[10]*m[13] - m[8]*m[1]*m[14] + m[8]*m[2]*m[13] + m[12]*m[1]*m[10] - m[12]*m[2]*m[9];
  inv[2]  =  m[1]*m[6]*m[15]  - m[1]*m[7]*m[14]  - m[5]*m[2]*m[15] + m[5]*m[3]*m[14] + m[13]*m[2]*m[7]  - m[13]*m[3]*m[6];
  inv[6]  = -m[0]*m[6]*m[15]  + m[0]*m[7]*m[14]  + m[4]*m[2]*m[15] - m[4]*m[3]*m[14] - m[12]*m[2]*m[7]  + m[12]*m[3]*m[6];
  inv[10] =  m[0]*m[5]*m[15]  - m[0]*m[7]*m[13]  - m[4]*m[1]*m[15] + m[4]*m[3]*m[13] + m[12]*m[1]*m[7]  - m[12]*m[3]*m[5];
  inv[14] = -m[0]*m[5]*m[14]  + m[0]*m[6]*m[13]  + m[4]*m[1]*m[14] - m[4]*m[2]*m[13] - m[12]*m[1]*m[6]  + m[12]*m[2]*m[5];
  inv[3]  = -m[1]*m[6]*m[11]  + m[1]*m[7]*m[10]  + m[5]*m[2]*m[11] - m[5]*m[3]*m[10] - m[9]*m[2]*m[7]   + m[9]*m[3]*m[6];
  inv[7]  =  m[0]*m[6]*m[11]  - m[0]*m[7]*m[10]  - m[4]*m[2]*m[11] + m[4]*m[3]*m[10] + m[8]*m[2]*m[7]   - m[8]*m[3]*m[6];
  inv[11] = -m[0]*m[5]*m[11]  + m[0]*m[7]*m[9]   + m[4]*m[1]*m[11] - m[4]*m[3]*m[9]  - m[8]*m[1]*m[7]   + m[8]*m[3]*m[5];
  inv[15] =  m[0]*m[5]*m[10]  - m[0]*m[6]*m[9]   - m[4]*m[1]*m[10] + m[4]*m[2]*m[9]  + m[8]*m[1]*m[6]   - m[8]*m[2]*m[5];
  let det = m[0]*inv[0] + m[1]*inv[4] + m[2]*inv[8] + m[3]*inv[12];
  if (det === 0) return new Float32Array(16);
  det = 1 / det;
  for (let i = 0; i < 16; i++) inv[i] *= det;
  return inv;
}

function normalize(v: Vec3) {
  const L = Math.hypot(v[0], v[1], v[2]) || 1;
  v[0] /= L; v[1] /= L; v[2] /= L;
}
function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}
function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
